import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicingState, Order } from 'models/order';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BoxWithHighLighting from 'components/with-hith-light-components/box-with-highlighting.component';

type DeliveryClinicProps = {
    order: Order;
    versionCheck: boolean;
    redirectToModifyOrder: (isResend: boolean) => void;
    isCustomer: boolean;
    isExternalTechnician: boolean;
};
const DeliveryClinic: FC<DeliveryClinicProps> = ({
    order,
    versionCheck,
    redirectToModifyOrder,
    isCustomer,
    isExternalTechnician,
}: DeliveryClinicProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'organization', 'transport', 'country-names']);

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    return (
        <Box mb={2}>
            <Box mb={1} display="flex" alignItems="flex-end">
                <Typography variant="h4">{translate('transport:PLACE_OF_DELIVERY')}</Typography>
                {!isExternalTechnician && versionCheck && !modifyLocked() && (!order.isCreatedByLab || !isCustomer) ? (
                    <Tooltip title={translate('orders:CHANGE_PLACE_OF_DELIVERY').toString()}>
                        <IconButton
                            onClick={() => redirectToModifyOrder(false)}
                            size="small"
                            color="primary"
                            data-test="delivery-section.edit-delivery-btn"
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : undefined}
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">{translate('common:NAME')}</Typography>
                    <BoxWithHighLighting
                        originalData={order.originalOrder.deliveryDetails.destination.name}
                        data={order.deliveryDetails.destination.name}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">{translate('organization:ADDRESS.ADDRESS')}</Typography>
                    <BoxWithHighLighting
                        originalData={order.originalOrder.deliveryDetails.destination.contact.address?.street as string}
                        data={order.deliveryDetails.destination.contact.address?.street as string}
                    />
                    <Box display="flex">
                        <BoxWithHighLighting
                            sx={{ marginRight: 0.5 }}
                            originalData={
                                order.originalOrder.deliveryDetails.destination.contact.address?.postalCode as string
                            }
                            data={order.deliveryDetails.destination.contact.address?.postalCode as string}
                        />
                        <BoxWithHighLighting
                            sx={{ marginRight: 0.5 }}
                            originalData={
                                order.originalOrder.deliveryDetails.destination.contact.address?.city as string
                            }
                            data={order.deliveryDetails.destination.contact.address?.city as string}
                        />
                        <BoxWithHighLighting
                            originalData={translate(
                                `country-names:COUNTRY_${order.originalOrder.deliveryDetails.destination.contact?.address?.country || ''}`,
                            )}
                            data={translate(
                                `country-names:COUNTRY_${order.deliveryDetails.destination.contact?.address?.country || ''}`,
                            )}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">{translate('organization:CONTACT_INFO')}</Typography>
                    <BoxWithHighLighting
                        originalData={
                            order.originalOrder.deliveryDetails.destination.contact?.communication?.phone as string
                        }
                        data={order.deliveryDetails.destination.contact?.communication?.phone as string}
                    />
                    <BoxWithHighLighting
                        originalData={
                            order.originalOrder.deliveryDetails.destination.contact?.communication?.email as string
                        }
                        data={order.deliveryDetails.destination.contact?.communication?.email as string}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default DeliveryClinic;
